<template>
  <div class="register-uf-view rcp-view">
    <div
      v-if="isInitializing"
      class="loading"
    >
      <unc-loader />
    </div>

    <template v-else-if="showNotFound">
      <div class="register-card rcp-card rr-notfound-card">
        <div class="rr-notfound-inner">
          <div class="rr-notfound-icon">
            <svg
              aria-hidden="true"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="12"
              />
              <line
                x1="12"
                y1="16"
                x2="12.01"
                y2="16"
              />
            </svg>
          </div>
          <h1 class="rr-notfound-title">
            No se pudo recuperar tu contraseña
          </h1>
          <p class="rr-notfound-desc">
            El enlace utilizado caducó o no es válido. Iniciá nuevamente el proceso de recuperación de contraseña para que volvamos a enviarte el correo.
          </p>

          <div class="rr-actions rr-actions--center">
            <button
              type="button"
              class="btn btn-primary waves-effect register-btn register-btn-primary rr-notfound-btn"
              style="width: auto;"
              @click.prevent="goRecoverPassword()"
            >
              <span>Ir a recuperar contraseña</span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="register-card rcp-card">
        <div class="register-glow" />
        <div class="card-header ch-alt register-header">
          <div class="register-kicker rr-kicker--info">
            Nueva contraseña
          </div>
          <h1 class="register-title">
            Recuperar contraseña
          </h1>
          <p class="register-subtitle">
            Creá una nueva contraseña segura para tu Usuario UNC.
          </p>
        </div>

        <div class="card-body register-body">
          <form @submit.prevent="validateBeforeSubmit">
            <div
              v-if="showRulesPassword && hasPasswordPolicyRules"
              class="rcp-rules-box"
            >
              <div class="rcp-rules-title">
                Reglas para contraseñas
              </div>
              <ul class="rcp-rules-list--password">
                <li
                  v-for="rule in passwordRulesDisplay"
                  :key="rule.key"
                  class="rcp-rule-item"
                  :class="{
                    'rcp-rule-item--neutral': rule.displayState === 'neutral',
                    'rcp-rule-item--success': rule.displayState === 'success',
                    'rcp-rule-item--pending': rule.displayState === 'pending',
                    'rcp-rule-item--info': rule.displayState === 'info',
                  }"
                >
                  <div class="rcp-rule-line">
                    <div class="rcp-rule-dot" />
                    <span class="rcp-rule-text">{{ rule.description }}</span>
                    <span class="rcp-rule-status">{{ rule.statusText }}</span>
                  </div>
                  <small
                    v-if="rule.helperText"
                    class="rcp-rule-helper"
                  >{{ rule.helperText }}</small>
                </li>
              </ul>
            </div>

            <div class="rcp-fields-grid">
              <div
                class="form-group register-field"
                :class="{ 'has-error': errors.has('password') }"
              >
                <div>
                  <label for="password">Ingresá tu nueva contraseña</label>
                  <div class="rcp-password-wrap">
                    <input
                      id="password"
                      v-model="item.password"
                      v-validate="passwordValidationRules || 'required'"
                      :type="passwordFieldType"
                      name="password"
                      class="form-control rcp-password-input"
                      placeholder="Ingresá una contraseña válida..."
                      value=""
                      autocomplete="new-password"
                      :aria-describedby="errors.has('password') ? 'password-error' : null"
                    >
                    <button
                      type="button"
                      class="rcp-eye-btn"
                      :aria-label="passwordFieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                      @click.prevent="showPassword(passwordFieldType === 'password')"
                    >
                      <svg
                        v-if="passwordFieldType === 'password'"
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <svg
                        v-else
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                </div>
                <small
                  v-if="errors.has('password')"
                  id="password-error"
                  class="help-block"
                  role="alert"
                >{{ errors.first('password') }}</small>
              </div>

              <div
                class="form-group register-field"
                :class="{ 'has-error': errors.has('passwordConfirmation') || showValidationErrorPasswordConfirmation }"
              >
                <div>
                  <label for="passwordConfirmation">Repetir contraseña</label>
                  <div class="rcp-password-wrap">
                    <input
                      id="passwordConfirmation"
                      v-model="item.passwordConfirmation"
                      v-validate="'required'"
                      :type="passwordConfirmationFieldType"
                      name="passwordConfirmation"
                      class="form-control rcp-password-input"
                      placeholder="Ingresá nuevamente tu contraseña..."
                      value=""
                      autocomplete="new-password"
                      :aria-describedby="errors.has('passwordConfirmation') ? 'passwordConfirmation-error' : null"
                      @keyup="showValidationErrorPasswordConfirmation = false"
                    >
                    <button
                      type="button"
                      class="rcp-eye-btn"
                      :aria-label="passwordConfirmationFieldType === 'password' ? 'Mostrar contraseña repetida' : 'Ocultar contraseña repetida'"
                      @click.prevent="showPassword(passwordConfirmationFieldType === 'password', true)"
                    >
                      <svg
                        v-if="passwordConfirmationFieldType === 'password'"
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <svg
                        v-else
                        aria-hidden="true"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                </div>
                <small
                  v-if="errors.has('passwordConfirmation')"
                  id="passwordConfirmation-error"
                  class="help-block"
                  role="alert"
                >{{
                  errors.first('passwordConfirmation') }}</small>
                <small
                  v-if="showValidationErrorPasswordConfirmation"
                  class="help-block"
                >Las contraseñas no
                  coinciden</small>
              </div>
            </div>

            <div class="rcp-actions">
              <button
                type="button"
                class="btn btn-link waves-effect register-btn register-btn-secondary"
                @click.prevent="goBack()"
              >
                Volver
              </button>
              <button
                type="submit"
                class="btn btn-primary waves-effect register-btn register-btn-primary"
              >
                <span>Guardar contraseña</span>
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line
                    x1="5"
                    y1="12"
                    x2="19"
                    y2="12"
                  />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { HttpYULI, HttpFRONT, swal } from '../../share.js';
import validateForm from '../../mixins/validate-form';

export default {
    name: 'RestoreChangePassword',
    mixins: [validateForm],
    props: {
        token: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            isInitializing: true,
            item: {
                password: '',
                passwordConfirmation: '',
            },
            user: '',
            showNotFound: false,
            showRulesPassword: true,
            showValidationErrorPasswordConfirmation: false,
            passwordFieldType: 'password',
            passwordConfirmationFieldType: 'password',
            passwordPolicy: null,
            backendRuleErrors: [],
            passwordPolicyState: 'loading', // 'loading' | 'ready' | 'error'
        };
    },
    computed: {
        hasPasswordPolicyRules () {
            return Array.isArray(this.passwordPolicy?.rules) && this.passwordPolicy.rules.length > 0;
        },
        canContinue () {
            return this.passwordPolicyState === 'ready';
        },
        passwordValidationRules () {
            if (this.passwordPolicyState !== 'ready') return '';

            const rules = ['required'];
            this.passwordPolicy?.rules?.forEach(rule => {
                if (rule.frontendValidation && rule.regex) {
                    rules.push(`password-${rule.name}`);
                }
            });
            return rules.join('|');
        },
        passwordRulesDisplay () {
            const rules = this.passwordPolicy?.rules;

            if (!Array.isArray(rules) || rules.length === 0) {
                return [];
            }

            const currentPassword = this.item.password || '';
            const hasPasswordInput = currentPassword.length > 0;
            const hasBackendRuleEvaluation = hasPasswordInput &&
                Array.isArray(this.backendRuleErrors) &&
                this.backendRuleErrors.length > 0;

            return rules.map((rule, index) => {
                const description = rule?.description || rule?.error || rule?.name || 'Regla de contraseña';
                const backendFailureMessage = hasPasswordInput
                    ? this.getBackendRuleFailureMessage(rule)
                    : '';
                const isFrontendRule = Boolean(rule?.frontendValidation && rule?.regex);

                if (hasBackendRuleEvaluation) {
                    if (backendFailureMessage) {
                        return {
                            key: `${rule?.name || 'rule'}-${index}`,
                            description,
                            displayState: 'pending',
                            statusText: 'No cumple',
                            helperText: 'La contraseña no cumple esta regla.',
                        };
                    }

                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'success',
                        statusText: 'Cumple',
                        helperText: '',
                    };
                }

                if (!isFrontendRule) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'info',
                        statusText: 'Se valida al crear',
                        helperText: 'Esto lo validaremos cuando aprietes el botón "Guardar contraseña".',
                    };
                }

                const { validRegex, matches } = this.testPasswordRuleRegex(rule.regex, currentPassword);

                if (!validRegex) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'info',
                        statusText: 'Se valida al crear',
                        helperText: 'No pudimos evaluar esta regla en el formulario. La validaremos al guardar tu contraseña.',
                    };
                }

                if (!hasPasswordInput) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'neutral',
                        statusText: 'Pendiente',
                        helperText: '',
                    };
                }

                if (matches) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'success',
                        statusText: 'Cumple',
                        helperText: '',
                    };
                }

                return {
                    key: `${rule?.name || 'rule'}-${index}`,
                    description,
                    displayState: 'pending',
                    statusText: 'Falta cumplir',
                    helperText: '',
                };
            });
        },
    },
    watch: {
        'item.password' () {
            this.backendRuleErrors = [];
        },
    },
    async mounted () {
        if (this.token) {
            this.isInitializing = true;
            await this.getData();
            await this.getPasswordPolicy();
            this.isInitializing = false;
        } else {
            this.showNotFound = true;
            this.isInitializing = false;
        }
    },
    methods: {
        async getData () {
            try {
                const response = await HttpYULI.get(`/v2/tokens/${this.token}/user`);
                if (response.status === 200) {
                    this.user = response.data.user;
                } else {
                    this.showNotFound = true;
                }
            } catch {
                this.showNotFound = true;
            }
        },
        normalizeRuleText (text = '') {
            return String(text)
                .toLowerCase()
                .trim();
        },
        getBackendRuleFailureMessage (rule) {
            if (!Array.isArray(this.backendRuleErrors) || this.backendRuleErrors.length === 0) {
                return '';
            }

            const ruleName = this.normalizeRuleText(rule?.name || '');
            const ruleError = this.normalizeRuleText(rule?.error || '');

            const matchedError = this.backendRuleErrors.find(detail => {
                const detailName = this.normalizeRuleText(detail?.name || '');
                const detailMessage = this.normalizeRuleText(detail?.message || '');

                if (detailName && ruleName && detailName === ruleName) {
                    return true;
                }

                if (detailMessage && ruleError) {
                    return detailMessage === ruleError || detailMessage.includes(ruleError) || ruleError.includes(detailMessage);
                }

                return false;
            });

            return matchedError?.message || '';
        },
        async getPasswordPolicy () {
            this.passwordPolicyState = 'loading';

            try {
                const response = await HttpYULI.get('/v2/auth/password-policy');
                this.passwordPolicy = response.data;

                // TEMPORARY: the API should NOT return the "password reused" rule here.
                const isPasswordReusedRule = (rule) => {
                    const name = String(rule?.name || '').toLowerCase();

                    return (
                        name === 'password-reused' ||
                        name.includes('password-reused')
                    );
                };

                if (Array.isArray(this.passwordPolicy?.rules)) {
                    this.passwordPolicy = {
                        ...this.passwordPolicy,
                        rules: this.passwordPolicy.rules.filter(rule => !isPasswordReusedRule(rule)),
                    };
                }

                this.passwordPolicy.rules?.forEach(rule => {
                    if (rule.frontendValidation && rule.regex) {
                        const validatorName = `password-${rule.name}`;
                        const regex = new RegExp(rule.regex);

                        this.$validator.extend(validatorName, {
                            getMessage: () => rule.error,
                            validate: (value) => regex.test(value),
                        });
                    }
                });
                this.passwordPolicyState = 'ready';
            } catch {
                this.passwordPolicy = null;
                this.passwordPolicyState = 'error';
                swal({
                    title: 'No pudimos cargar las reglas de seguridad',
                    text: 'No pudimos obtener las reglas de seguridad para contraseñas. Por favor, intentá nuevamente más tarde.',
                    type: 'warning',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });
            }
        },
        async validateBeforeSubmit () {
            if (!this.canContinue) {
                swal({
                    title: 'No pudimos cargar las reglas de seguridad',
                    text: 'No pudimos obtener las reglas de seguridad para contraseñas. Por favor, intentá nuevamente más tarde.',
                    type: 'warning',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });
                return;
            }

            try {
                if (await this.validateForm()) {
                    if (this.validatePasswordConfirmation(this.item.password, this.item.passwordConfirmation)) {
                        await this.save();
                    } else {
                        this.showValidationErrorPasswordConfirmation = true;
                    }
                }
            } catch {
                swal({ title: 'Algo salió mal', text: 'Tus datos no fueron registrados.', type: 'warning' });
            }
        },
        validatePasswordConfirmation (password, passwordConfirmation) {
            return password === passwordConfirmation;
        },
        async save () {
            this.isLoading = true;

            const userToSend = {
                token: this.token,
                password: this.item.password,
            };

            try {
                const response = await HttpFRONT.patch(`usuarios/${this.user._id}/recuperar_clave`, userToSend);

                if (response.status === 201 || response.status === 200) {
                    swal({ title: 'Contraseña recuperada', text: 'Tu contraseña fue recuperada satisfactoriamente.', type: 'success' });
                    this.$router.push('/');
                }
            } catch (error) {
                const status = error.response?.status;

                switch (status) {
                case 401:
                    swal({
                        title: 'Acceso denegado',
                        text: 'No posee permisos para acceder a este servicio.',
                        type: 'warning',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    break;
                case 422: {
                    const data = (error.response && error.response.data) || {};

                    const details = data.details;
                    const list = Array.isArray(details) ? details : (details ? [details] : []);
                    this.backendRuleErrors = list;
                    const messages = list
                        .map(d => d && d.message)
                        .filter(Boolean);

                    let text;

                    if (error.response?.data === 'El usuario no está activo.') {
                        text = 'El Usuario UNC ingresado no se encuentra activo.';
                    } else if (messages.length === 0) {
                        text = 'Tu contraseña no cumple con las políticas de seguridad.';
                    } else {
                        text = messages.join('\n');
                    }

                    swal({
                        title: 'No pudimos cambiar la contraseña',
                        text,
                        type: 'warning',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    break;
                }
                default:
                    swal({
                        title: 'Algo salió mal',
                        text: 'Servicio temporalmente no disponible.',
                        type: 'warning',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    break;
                }
            } finally {
                this.isLoading = false;
            }
        },
        goBack () {
            this.$router.push('/');
        },
        goRecoverPassword () {
            this.$router.push('/recuperar-clave');
        },
        testPasswordRuleRegex (regexPattern, passwordValue) {
            try {
                const regex = new RegExp(regexPattern);
                return {
                    validRegex: true,
                    matches: regex.test(passwordValue),
                };
            } catch {
                return {
                    validRegex: false,
                    matches: false,
                };
            }
        },
        showPassword (show = true, confirmation = false) {
            const fieldType = show ? 'text' : 'password';

            if (confirmation) {
                this.passwordConfirmationFieldType = fieldType;
            } else {
                this.passwordFieldType = fieldType;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.rcp-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);

  font-family: var(--font-body);
}

.register-uf-view {
  padding: 32px 16px 0;
}

.rcp-card {
  width: 100%;
  max-width: $register-card-wide;
  margin: 0 auto;
}

.register-field {
  margin-bottom: 18px;
}

.rcp-rules-box {
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(10, 95, 150, 0.12);
  background: rgba(10, 95, 150, 0.04);
}

.rcp-rules-title {
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--register-azul-mid);
  margin-bottom: 8px;
}

.rcp-rules-list--password {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rcp-rule-item {
  border: 1px solid rgba(10, 95, 150, 0.15);
  border-radius: 10px;
  padding: 6px 8px;
  background: rgba(10, 95, 150, 0.04);
}

.rcp-rule-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.rcp-rule-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background: #7f91a5;
}

.rcp-rule-text {
  flex: 1;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.55;
}

.rcp-rule-status {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.rcp-rule-helper {
  display: block;
  margin-top: 6px;
  margin-left: 16px;
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.5;
  color: #5a6d82;
}

.rcp-rule-item--neutral {
  border-color: rgba(86, 107, 132, 0.25);
  background: rgba(86, 107, 132, 0.07);
  color: #3f556f;
}

.rcp-rule-item--neutral .rcp-rule-dot {
  background: #6f8196;
}

.rcp-rule-item--neutral .rcp-rule-status {
  color: #5c6f84;
}

.rcp-rule-item--success {
  border-color: rgba(46, 141, 89, 0.36);
  background: rgba(46, 141, 89, 0.08);
  color: #1f5d3c;
}

.rcp-rule-item--success .rcp-rule-dot {
  background: #2e8d59;
}

.rcp-rule-item--success .rcp-rule-status {
  color: #2a7f50;
}

.rcp-rule-item--pending {
  border-color: rgba(218, 128, 18, 0.42);
  background: rgba(218, 128, 18, 0.09);
  color: #8a4f06;
}

.rcp-rule-item--pending .rcp-rule-dot {
  background: #d18314;
}

.rcp-rule-item--pending .rcp-rule-status {
  color: #b7680d;
}

.rcp-rule-item--info {
  border-color: rgba(26, 116, 162, 0.3);
  background: rgba(26, 116, 162, 0.08);
  color: #1c5f86;
}

.rcp-rule-item--info .rcp-rule-dot {
  background: #1a74a2;
}

.rcp-rule-item--info .rcp-rule-status {
  color: #206992;
}

.rcp-fields-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  margin-bottom: 0;
}

.rcp-password-wrap {
  position: relative;
}

.rcp-password-input {
  padding-right: 50px !important;
}

.rcp-eye-btn {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 56, 101, 0.06);
  color: var(--register-azul-mid);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

.rcp-eye-btn:hover {
  background: rgba(62, 182, 184, 0.14);
  color: var(--register-azul);
}

.rcp-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 0;
}

@media (max-width: 720px) {
  .rcp-fields-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .register-uf-view {
    padding: 24px 12px 0;
  }

  .rcp-actions {
    justify-content: stretch;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .rcp-actions .register-btn,
  .rr-actions .register-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
